/**
 * ============================================================
 * PGLP | MONTE HERMOM
 * SERVICE: AssetPericialService
 * ------------------------------------------------------------
 * Processa imagens de vistoria pericial:
 *   ✔ Marca d'água técnica (Lead Gray #2F353B)
 *   ✔ Hash SHA-256 real (crypto.subtle) — nunca Math.random
 *   ✔ Geolocalização via API do navegador
 *   ✔ Nomeação determinística por hash + timestamp
 *   ✔ Mapeamento de pastas via constants/vistoria
 *
 * COMPATIBILIDADE:
 *   processarCaptura(file, fatorId, numeroLaudo)        ← assinatura antiga
 *   processarCaptura(file, fatorId, numeroLaudo, opts)  ← nova, com GPS/local
 *
 * DIRETRIZ: NBR 14653 | Zero mock | Zero placeholder
 * VERSÃO: 4.0.0
 * ============================================================
 */

import { FATORES_CONFIG } from '@/constants/vistoria';

/* =========================
   CONSTANTES
========================= */

const LEAD_GRAY = '#2F353B';
const WATERMARK_PADDING = 32;
const WATERMARK_LINE_HEIGHT = 42;
const JPEG_QUALITY = 0.92;
const MIN_MEGAPIXELS = 1.2;

/* =========================
   HASH SHA-256 REAL
========================= */

/**
 * Calcula SHA-256 de um ArrayBuffer ou Blob.
 * Requer contexto seguro (HTTPS ou localhost).
 * @param {ArrayBuffer|Blob} entrada
 * @returns {Promise<string>} hash hexadecimal
 */
async function calcularHash(entrada) {
  if (!crypto?.subtle) {
    throw new Error(
      'crypto.subtle indisponível — HTTPS obrigatório para gerar hash de integridade.'
    );
  }

  let buffer;
  if (entrada instanceof Blob) {
    buffer = await entrada.arrayBuffer();
  } else if (entrada instanceof ArrayBuffer) {
    buffer = entrada;
  } else {
    throw new Error('calcularHash: esperado Blob ou ArrayBuffer');
  }

  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/* =========================
   VALIDAÇÃO DE RESOLUÇÃO
========================= */

function validarResolucao(largura, altura) {
  const mp = (largura * altura) / 1_000_000;
  if (mp < MIN_MEGAPIXELS) {
    throw new Error(
      `Resolução insuficiente: ${mp.toFixed(2)}MP (mínimo ${MIN_MEGAPIXELS}MP para conformidade pericial)`
    );
  }
  return +mp.toFixed(2);
}

/* =========================
   MARCA D'ÁGUA TÉCNICA
========================= */

/**
 * Desenha rodapé técnico em Lead Gray sobre o canvas.
 * Formato: [CONFIDENCIAL] REPORT | SLOT | LOCAL | GPS | DATA | PERITO
 */
function aplicarMarcaDagua(ctx, largura, altura, linhas) {
  const alturaFaixa = linhas.length * WATERMARK_LINE_HEIGHT + WATERMARK_PADDING * 2;

  // Faixa semi-transparente
  ctx.save();
  ctx.fillStyle = 'rgba(47, 53, 59, 0.82)'; // Lead Gray com opacidade
  ctx.fillRect(0, altura - alturaFaixa, largura, alturaFaixa);

  // Linhas de texto
  const fontSize = Math.max(14, Math.floor(altura * 0.018));
  ctx.font = `900 italic ${fontSize}px Inter, Arial, sans-serif`;
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  linhas.forEach((texto, i) => {
    const y = altura - alturaFaixa + WATERMARK_PADDING + i * WATERMARK_LINE_HEIGHT;
    ctx.fillText(texto.toUpperCase(), WATERMARK_PADDING, y);
  });

  ctx.restore();
}

/* =========================
   BLOB → BASE64
========================= */

function blobParaBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Falha ao converter blob para base64'));
    reader.readAsDataURL(blob);
  });
}

/* =========================
   NOMEAÇÃO DETERMINÍSTICA
========================= */

/**
 * Gera nome de arquivo sem Math.random.
 * Formato: {numeroLaudo}_{FXX}_{hash8}_{YYYYMMDD}.JPG
 * O hash vem do conteúdo da imagem — reenvios idênticos geram o mesmo nome.
 */
function gerarNomeArquivo(numeroLaudo, fatorId, hash) {
  const dataSufixo = new Date().toISOString().split('T')[0].replace(/-/g, '');
  const hashCurto = hash.substring(0, 8).toUpperCase();
  const codigo = (FATORES_CONFIG[fatorId]?.codigo) || fatorId.toUpperCase();
  return `${numeroLaudo}_${codigo}_${hashCurto}_${dataSufixo}.JPG`;
}

/* =========================
   GEOLOCALIZAÇÃO REAL
========================= */

/**
 * Obtém coordenadas do dispositivo.
 * @param {object} opcoes — { timeout, enableHighAccuracy }
 * @returns {Promise<{lat: string, lng: string, precisao: number}>}
 */
function obterCoordenadas(opcoes = {}) {
  const {
    timeout = 10000,
    enableHighAccuracy = true,
  } = opcoes;

  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocalização não suportada neste dispositivo'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      pos => resolve({
        lat: pos.coords.latitude.toFixed(6),
        lng: pos.coords.longitude.toFixed(6),
        precisao: Math.round(pos.coords.accuracy),
      }),
      err => {
        const mensagens = {
          1: 'Permissão de GPS negada pelo usuário',
          2: 'Posição indisponível — verifique sinal do GPS',
          3: 'Timeout ao obter coordenadas',
        };
        reject(new Error(mensagens[err.code] || `GPS erro: ${err.message}`));
      },
      { enableHighAccuracy, timeout, maximumAge: 0 }
    );
  });
}

/* =========================
   PROCESSAMENTO PRINCIPAL
========================= */

/**
 * Processa captura pericial completa.
 *
 * @param {File}   arquivoBruto     — imagem original
 * @param {string} fatorId          — 'f01_servicos', 'f02_infra', etc.
 * @param {string} numeroLaudo      — ex: 'MH-2026-0042'
 * @param {object} opcoes           — { perito, comodoLocal, coordenadas, tentarGPS }
 *
 * @returns {Promise<{
 *   url: string,
 *   file: string,             // alias de url (compatibilidade)
 *   blob: Blob,
 *   fileName: string,
 *   folder: string,
 *   timestamp: string,
 *   fator: string,
 *   hashIntegridade: string,  // SHA-256 do arquivo final
 *   hashOriginal: string,     // SHA-256 do arquivo original
 *   metadados: object,
 * }>}
 */
async function processarCaptura(arquivoBruto, fatorId, numeroLaudo, opcoes = {}) {
  if (!(arquivoBruto instanceof File)) {
    throw new Error('processarCaptura: primeiro parâmetro deve ser um File');
  }
  if (!fatorId) {
    throw new Error('processarCaptura: fatorId obrigatório');
  }
  if (!numeroLaudo) {
    throw new Error('processarCaptura: numeroLaudo obrigatório');
  }

  const {
    perito = null,
    comodoLocal = null,
    coordenadas = null,
    tentarGPS = false,
  } = opcoes;

  // 1. Hash do arquivo ORIGINAL (antes de qualquer transformação)
  const hashOriginal = await calcularHash(arquivoBruto);

  // 2. Decodifica bitmap
  const bitmap = await createImageBitmap(arquivoBruto);
  const megapixels = validarResolucao(bitmap.width, bitmap.height);

  // 3. Monta canvas
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0);

  // 4. Coordenadas: prioriza o que veio por parâmetro; se não, tenta GPS
  let coordsFinal = coordenadas;
  if (!coordsFinal && tentarGPS) {
    try {
      coordsFinal = await obterCoordenadas();
    } catch {
      coordsFinal = null; // segue sem GPS — não bloqueia captura
    }
  }

  // 5. Monta linhas da marca d'água
  const timestamp = new Date().toLocaleString('pt-BR');
  const codigoFator = FATORES_CONFIG[fatorId]?.codigo || fatorId.toUpperCase();
  const linhas = [
    `CONFIDENCIAL — LAUDO ${numeroLaudo}`,
    `MÓDULO ${codigoFator} — ${FATORES_CONFIG[fatorId]?.titulo || fatorId}`,
  ];
  if (comodoLocal) linhas.push(`LOCAL: ${comodoLocal}`);
  if (coordsFinal) {
    linhas.push(`GPS: ${coordsFinal.lat}, ${coordsFinal.lng} (±${coordsFinal.precisao || '?'}m)`);
  }
  linhas.push(`DATA: ${timestamp}`);
  if (perito) linhas.push(`PERITO: ${perito}`);

  aplicarMarcaDagua(ctx, canvas.width, canvas.height, linhas);

  // 6. Exporta JPEG
  const blobMarcado = await new Promise((resolve, reject) =>
    canvas.toBlob(
      b => (b ? resolve(b) : reject(new Error('Falha ao exportar canvas para JPEG'))),
      'image/jpeg',
      JPEG_QUALITY
    )
  );

  // 7. Hash do arquivo FINAL (com marca d'água) — este é o que vai para auditoria
  const hashIntegridade = await calcularHash(blobMarcado);

  // 8. Base64 para uso em <img src>
  const url = await blobParaBase64(blobMarcado);

  // 9. Nome determinístico
  const fileName = gerarNomeArquivo(numeroLaudo, fatorId, hashIntegridade);

  // 10. Pasta canônica via constants
  const folder = mapearPasta(fatorId);

  // 11. Libera recursos
  bitmap.close();

  // 12. Retorno
  const metadados = {
    fatorId,
    codigoFator,
    numeroLaudo,
    hashOriginal,
    hashIntegridade,
    nomeOriginal: arquivoBruto.name,
    tamanhoOriginal: arquivoBruto.size,
    tamanhoFinal: blobMarcado.size,
    largura: canvas.width,
    altura: canvas.height,
    megapixels,
    mimeType: 'image/jpeg',
    timestamp: new Date().toISOString(),
    perito,
    comodoLocal,
    coordenadas: coordsFinal,
    marcaDagua: linhas.join(' | '),
    pasta: folder,
    nomeArquivo: fileName,
  };

  return {
    // Compatibilidade com código existente
    file: url,
    fileName,
    folder,
    timestamp,
    fator: fatorId,

    // Novos campos
    url,
    blob: blobMarcado,
    hashOriginal,
    hashIntegridade,
    metadados,
  };
}

/* =========================
   MAPEAMENTO DE PASTAS
========================= */

/**
 * Retorna o nome canônico da pasta do fator.
 * Fonte: constants/vistoria.js
 */
function mapearPasta(fatorId) {
  const config = FATORES_CONFIG[fatorId];
  return config?.pasta || 'ANEXOS_GERAIS';
}

/* =========================
   EXPORT
========================= */

const AssetPericialService = {
  processarCaptura,
  mapearPasta,
  obterCoordenadas,
  calcularHash,
};

export { calcularHash, obterCoordenadas };
export default AssetPericialService;